/**
 * Collaborative Work
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/collaborative-work
 * Backend: personal-collaboration.routes.ts (personal-scoped). Shared task lists with
 * friends/family/groups — not business projects.
 */
import React, { useEffect, useState } from 'react';
import ComingSoonPage from '../components/ComingSoonPage';
import { personalCollaborationApi, type PersonalTaskList } from '../services/api';

const CollaborativeWork: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lists, setLists] = useState<PersonalTaskList[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingListId, setDeletingListId] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState<Record<string, string>>({});
  const [addingTaskListId, setAddingTaskListId] = useState<string | null>(null);
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res: any = await personalCollaborationApi.listLists();
      if (res?.success && Array.isArray(res?.data)) {
        setLists(res.data);
      } else if (res?.error) {
        setError(res.error);
      }
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddList = async () => {
    if (!newTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res: any = await personalCollaborationApi.createList({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
      });
      if (res?.success) {
        setNewTitle('');
        setNewDescription('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed to create list');
    } catch {
      setError('Failed to create list');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteList = async (id: string) => {
    setDeletingListId(id);
    setError(null);
    try {
      const res: any = await personalCollaborationApi.deleteList(id);
      if (res?.success) loadData(true);
      else setError(res?.error || 'Failed to delete list');
    } catch {
      setError('Failed to delete list');
    } finally {
      setDeletingListId(null);
    }
  };

  const handleAddTask = async (listId: string) => {
    const title = (newTaskTitle[listId] || '').trim();
    if (!title) return;
    setAddingTaskListId(listId);
    setError(null);
    try {
      const res: any = await personalCollaborationApi.addTask(listId, { title });
      if (res?.success) {
        setNewTaskTitle((prev) => ({ ...prev, [listId]: '' }));
        loadData(true);
      } else setError(res?.error || 'Failed to add task');
    } catch {
      setError('Failed to add task');
    } finally {
      setAddingTaskListId(null);
    }
  };

  const handleToggleTask = async (taskId: string, isDone: boolean) => {
    setBusyTaskId(taskId);
    setError(null);
    try {
      const res: any = await personalCollaborationApi.updateTask(taskId, { isDone: !isDone });
      if (res?.success) loadData(true);
      else setError(res?.error || 'Failed to update task');
    } catch {
      setError('Failed to update task');
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    setBusyTaskId(taskId);
    setError(null);
    try {
      const res: any = await personalCollaborationApi.deleteTask(taskId);
      if (res?.success) loadData(true);
      else setError(res?.error || 'Failed to delete task');
    } catch {
      setError('Failed to delete task');
    } finally {
      setBusyTaskId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading…
      </div>
    );
  }

  if (error && lists.length === 0) {
    return (
      <ComingSoonPage
        title="Collaborative Work"
        icon="🤝"
        subtitle="Shared task lists with friends, family, and groups"
        description={error}
        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      />
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>🤝 Collaborative Work</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Shared task lists with friends, family, and groups
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowAdd((s) => !s)}
              style={{
                padding: '8px 16px',
                background: 'rgba(76,175,80,0.9)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Add List
            </button>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e2e8f0',
        }}>
          <input
            type="text"
            placeholder="List title (e.g. Weekend Trip Planning)"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddList}
              disabled={submitting || !newTitle.trim()}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {submitting ? 'Adding…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewTitle(''); setNewDescription(''); }}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}

      {lists.length === 0 && !error && !showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          <p style={{ margin: '0 0 12px 0' }}>No lists yet. Create a shared task list with friends, family, or a group.</p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            style={{
              padding: '8px 16px',
              background: '#667eea',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Create your first list
          </button>
        </div>
      )}

      {lists.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {lists.map((list) => {
            const tasks = list.tasks || [];
            const doneCount = tasks.filter((t) => t.isDone).length;
            return (
              <div
                key={list.id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '18px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  border: '1px solid #eee',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ fontSize: '16px', color: '#333' }}>{list.title}</strong>
                    {list.description && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{list.description}</p>
                    )}
                    <span style={{ fontSize: '12px', color: '#888' }}>{doneCount}/{tasks.length} done</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteList(list.id)}
                    disabled={deletingListId === list.id}
                    style={{
                      padding: '6px 12px',
                      background: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: deletingListId === list.id ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {deletingListId === list.id ? '…' : 'Delete List'}
                  </button>
                </div>

                <ul style={{ listStyle: 'none', margin: '12px 0 0 0', padding: 0 }}>
                  {tasks.map((task) => (
                    <li
                      key={task.id}
                      style={{
                        padding: '8px 0',
                        borderTop: '1px solid #f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={task.isDone}
                        disabled={busyTaskId === task.id}
                        onChange={() => handleToggleTask(task.id, task.isDone)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{
                        flex: 1,
                        fontSize: '14px',
                        color: task.isDone ? '#94a3b8' : '#333',
                        textDecoration: task.isDone ? 'line-through' : 'none',
                      }}>
                        {task.title}
                      </span>
                      {task.dueDate && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          {new Date(task.dueDate).toLocaleDateString(undefined, { dateStyle: 'short' })}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteTask(task.id)}
                        disabled={busyTaskId === task.id}
                        style={{
                          padding: '4px 10px',
                          background: '#f1f5f9',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#dc2626',
                          cursor: busyTaskId === task.id ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Delete
                      </button>
                    </li>
                  ))}
                </ul>

                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <input
                    type="text"
                    placeholder="Add a task…"
                    value={newTaskTitle[list.id] || ''}
                    onChange={(e) => setNewTaskTitle((prev) => ({ ...prev, [list.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddTask(list.id)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddTask(list.id)}
                    disabled={addingTaskListId === list.id || !(newTaskTitle[list.id] || '').trim()}
                    style={{
                      padding: '8px 14px',
                      background: '#667eea',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      cursor: addingTaskListId === list.id ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    {addingTaskListId === list.id ? 'Adding…' : 'Add'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CollaborativeWork;
